/**
 * Agent WebSocket API 인터페이스 레이어
 * WebSocketManager를 래핑하여 Agent 전용 기능 제공
 */

import { WebSocketManager } from "@/shared/lib/websocket-manager";
import { generateUUID } from "@/shared/utils/uuid";
import { AGENT_WEBSOCKET_ENDPOINT } from "../const/endpoint";
import type {
  ClientMessage,
  UserMessage,
  InterruptMessage,
  GetAvatarStatusMessage,
  AvatarSetupMessage,
  AvatarConfig,
  AvatarSetupData,
} from "../types/websocket-messages";
import type { MessageMode, LanguageCode, DisplayMode } from "../types/websocket-messages";
import type { RealtimeProvider } from "@/shared/constants/realtime-providers";

export interface AgentWebSocketConfig {
  endpoint?: string;
  sessionId?: string;
  autoReconnect?: boolean;
  maxRetries?: number;
}

export class AgentWebSocketAPI {
  private wsManager: WebSocketManager;
  private sessionId: string;

  constructor(config: AgentWebSocketConfig = {}) {
    this.sessionId = config.sessionId || generateUUID();

    const endpoint = config.endpoint || AGENT_WEBSOCKET_ENDPOINT;
    const wsUrl = `${endpoint}/${this.sessionId}`;

    this.wsManager = new WebSocketManager({
      url: wsUrl,
      autoReconnect: config.autoReconnect ?? true,
      maxRetries: config.maxRetries ?? 5,
    });
  }

  /**
   * WebSocket Manager 인스턴스 접근
   */
  get manager(): WebSocketManager {
    return this.wsManager;
  }

  /**
   * 세션 ID 접근
   */
  get currentSessionId(): string {
    return this.sessionId;
  }

  /**
   * 연결 상태
   */
  get isConnected(): boolean {
    return this.wsManager.isConnected;
  }

  /**
   * 연결 시작
   */
  async connect(): Promise<void> {
    return this.wsManager.connect();
  }

  /**
   * 연결 종료
   */
  disconnect(): void {
    this.wsManager.disconnect();
  }

  /**
   * 아바타 설정 메시지 전송
   * @param agentId - Agent UUID
   * @param avatar - 아바타 설정 (avatar_name, avatar_id, voice_id 등)
   * @param token - Clerk JWT 토큰 (Realtime API 인증용, optional)
   * @param realtimeProvider - Realtime Provider (none, openai, google) (optional)
   */
  sendAvatarSetup(
    agentId: string,
    avatar: AvatarConfig,
    token?: string,
    realtimeProvider?: RealtimeProvider,
    language?: LanguageCode,
  ): boolean {
    const pageLanguage = typeof document !== 'undefined'
      ? document.documentElement.lang.split('-')[0]
      : 'ko';
    const setupLanguage = language
      ?? (['ko', 'en', 'zh', 'ja'].includes(pageLanguage) ? pageLanguage as LanguageCode : 'ko');
    const message: AvatarSetupMessage = {
      type: "avatar_setup",
      data: {
        session_id: this.sessionId,
        agent_id: agentId,
        avatar,
        ...(token && { token }),  // token이 있을 때만 포함
        language: setupLanguage,
        realtime_provider: realtimeProvider ?? 'none',  // Realtime Voice Provider
        use_realtime_api: realtimeProvider && realtimeProvider !== 'none',  // 하위 호환성
      },
    };

    console.log("📷 아바타 설정 전송:", { ...message, data: { ...message.data, token: token ? "[MASKED]" : undefined } });
    return this.wsManager.send(message);
  }

  /**
   * 사용자 메시지 전송
   * @param params.displayMode - 웹/키오스크 모드 (web, kiosk) - Backend로 전달
   * @param params.agentId - Agent UUID - Backend로 전달
   * @param params.userId - User ID - Backend로 전달
   */
  sendUserMessage(params: {
    turnId: string;
    mode: MessageMode;
    data: string;
    language?: LanguageCode;
    displayMode?: DisplayMode;  // 웹/키오스크 모드 (web, kiosk)
    agentId?: string;      // Agent UUID
    userId?: string;       // User ID
  }): boolean {
    const message: UserMessage = {
      type: "message",
      data: {
        session_id: this.sessionId,
        turn_id: params.turnId,
        mode: params.mode,
        data: params.data,
        language: params.language,
        display_mode: params.displayMode,  // Backend로 전달
        agent_id: params.agentId,          // Backend로 전달
        user_id: params.userId,            // Backend로 전달
      },
    };

    console.log("💬 사용자 메시지 전송:", { ...message, displayMode: params.displayMode, agentId: params.agentId });
    return this.wsManager.send(message);
  }

  /**
   * 중단 메시지 전송
   */
  sendInterrupt(turnId: string): boolean {
    const message: InterruptMessage = {
      type: "interrupt",
      data: {
        session_id: this.sessionId,
        turn_id: turnId,
      },
    };

    console.log("⏸️ 중단 메시지 전송:", message);
    return this.wsManager.send(message);
  }

  /**
   * 아바타 상태 요청
   */
  requestAvatarStatus(): boolean {
    const message: GetAvatarStatusMessage = {
      type: "get_avatar_status",
    };

    console.log("📷 아바타 상태 요청:", message);
    return this.wsManager.send(message);
  }

  /**
   * 메시지 전송 (범용)
   */
  send(message: ClientMessage): boolean {
    return this.wsManager.send(message);
  }

  /**
   * 이벤트 리스너 등록
   */
  on<T = unknown>(event: string, listener: (data: T) => void): void {
    this.wsManager.on(event, listener);
  }

  /**
   * 이벤트 리스너 제거
   */
  off<T = unknown>(event: string, listener?: (data: T) => void): void {
    if (listener) {
      this.wsManager.off(event, listener);
    } else {
      this.wsManager.removeAllListeners(event);
    }
  }

  /**
   * 모든 리스너 제거
   */
  removeAllListeners(): void {
    this.wsManager.removeAllListeners();
  }

  /**
   * 자동 재연결 설정
   */
  setAutoReconnect(enabled: boolean): void {
    this.wsManager.setAutoReconnect(enabled);
  }

  /**
   * 재시도 카운트 초기화
   */
  resetRetryCount(): void {
    this.wsManager.resetRetryCount();
  }

  /**
   * 리소스 정리
   */
  destroy(): void {
    this.wsManager.destroy();
  }
}
